import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useAuthStore from '../stores/authStore';
import GoogleSignInButton from '../components/GoogleSignInButton';
import './Auth.css';

const SignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '';
  const { signin, isLoading, error, clearError } = useAuthStore();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [googleError, setGoogleError] = useState('');

  // Admin panel uses a separate login at /admin-signin
  useEffect(() => {
    if (
      nextPath === '/admin-cofounder-hubs' ||
      nextPath.startsWith('/admin-cofounder') ||
      nextPath === '/admin' ||
      nextPath.startsWith('/admin/')
    ) {
      navigate(`/admin-signin?next=${encodeURIComponent(nextPath || '/admin-cofounder-hubs')}`, {
        replace: true,
      });
    }
  }, [nextPath, navigate]);

  const goAfterSignIn = (signedInUser) => {
    const isEmployer = signedInUser?.accountType === 'employer';
    const hasCandidateProfileName = Boolean(signedInUser?.fullName?.trim());
    if (!isEmployer && !hasCandidateProfileName) {
      navigate('/onboarding');
    } else {
      navigate('/');
    }
  };

  const handleChange = (e) => {
    clearError();
    setGoogleError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await signin(formData.email, formData.password);
    if (result.success) {
      goAfterSignIn(result.user);
    } else if (result.error && result.error.includes('verify your email')) {
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    }
  };

  return (
    <Box className="auth-page-container ch-page-enter">
      <Box className="auth-page-main">
        <Fade in timeout={220}>
          <Box className="auth-page-stack ch-card-enter">
            <Link
              component={RouterLink}
              to="/"
              underline="none"
              color="primary"
              className="auth-page-back-link"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                alignSelf: 'flex-start',
                mb: 1.25,
              }}
            >
              <ArrowBackIcon fontSize="small" />
              Back to Home
            </Link>

            <Paper className="auth-page-box" elevation={0}>
              <Typography variant="h5" component="h1" align="center" sx={{ mb: 2 }}>
                Sign in to CofounderHubs
              </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.25}>
                {error && <Alert severity="error">{error}</Alert>}
                {googleError && <Alert severity="error">{googleError}</Alert>}

                <TextField
                  label="Email address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Password
                    </Typography>
                    <Link component={RouterLink} to="/forgot-password" underline="hover" variant="caption" color="primary">
                      Forgot password?
                    </Link>
                  </Box>
                  <TextField
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: false }}
                    label=""
                    placeholder="Password"
                  />
                </Box>

                <Button type="submit" variant="contained" color="primary" disabled={isLoading} fullWidth>
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>

                <Divider>
                  <Typography variant="caption" color="text.secondary">
                    or
                  </Typography>
                </Divider>

                <GoogleSignInButton onError={setGoogleError} />
              </Stack>
            </Box>

            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="body2">
                New to CofounderHubs?{' '}
                <Link component={RouterLink} to="/signup" underline="hover" color="primary" fontWeight={500}>
                  Create an account
                </Link>
              </Typography>
            </Box>
            </Paper>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default SignIn;
